/**
 * Formatting that the whole interface shares, so that the same value never
 * appears two ways on two screens (DESIGN_LANGUAGE.md §1, "Consistent").
 */

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

const ABSOLUTE = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

/**
 * "3 minutes ago", in the user's locale.
 *
 * Relative time is what people read in an activity feed; the absolute time is
 * what they need when they are reconstructing an incident. Both are rendered —
 * this one as the text, `absoluteTime` as the `title` and `<time datetime>` —
 * so neither is lost. A relative time with no absolute behind it is unusable in
 * a postmortem, and an absolute time alone is unreadable in a feed.
 */
export function relativeTime(value: string | Date, now: Date = new Date()): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';

  let duration = (date.getTime() - now.getTime()) / 1000;
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return RELATIVE.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return '';
}

const DATE_ONLY = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

/**
 * A date with no time on it.
 *
 * For values where the time is noise rather than information: when an account
 * was created, when a token expires. "Oct 6, 2026, 06:14 AM" invites the
 * reader to work out whether 06:14 matters, and it never does.
 */
export function absoluteDate(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '' : DATE_ONLY.format(date);
}

export function absoluteTime(value: string | Date): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '' : ABSOLUTE.format(date);
}

/** `12`, `1,204`, `12.4k` — the last only above five digits, where the digits stop being read. */
export function compactCount(value: number): string {
  if (value < 100_000) return new Intl.NumberFormat().format(value);
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

/**
 * The acronyms in this product's vocabulary, which sentence-casing ruins.
 *
 * Taken from `SecurityFindingCategory`, `SecurityScanKind` and the event
 * catalogue — the words that appear in `action`, `event_type` and `category`
 * values. An unlisted token is sentence-cased as before, which is the right
 * default for an open-ended field.
 */
const ACRONYMS = new Map([
  ['sast', 'SAST'],
  ['dast', 'DAST'],
  ['iac', 'IaC'],
  ['sbom', 'SBOM'],
  ['sca', 'SCA'],
  ['api', 'API'],
  ['ci', 'CI'],
  ['cd', 'CD'],
  ['mfa', 'MFA'],
  ['totp', 'TOTP'],
  ['pr', 'PR'],
  ['url', 'URL'],
  ['ssh', 'SSH'],
  ['lfs', 'LFS'],
  ['rls', 'RLS'],
]);

/**
 * Turn `pull_request.merged` into "Pull request merged".
 *
 * Both `ActivityEntry.action` and `Notification.event_type` are documented as
 * open-ended — "tolerate unknown values" — so this has to render an action it
 * has never seen rather than fall back to "Unknown". Replacing the separators
 * and sentence-casing does that: an unrecognised `foo_bar.baz` reads as "Foo bar
 * baz", which is wrong in register and right in substance, and is a great deal
 * better than hiding the event.
 */
export function humanizeToken(token: string): string {
  const words = token
    .replace(/^com\.exigit\./, '')
    .replace(/[._]/g, ' ')
    .trim();
  if (!words) return '';

  const parts = words.split(' ').map((word) => ACRONYMS.get(word.toLowerCase()) ?? word);
  const first = parts[0] ?? '';
  const head = ACRONYMS.has(first.toLowerCase())
    ? first
    : first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...parts.slice(1)].join(' ');
}

/** The first one or two initials of a name, for an avatar fallback. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0] ?? '').charAt(0) + (parts[parts.length - 1] ?? '').charAt(0)).toUpperCase();
}
